import { tokenizeFormatText } from '../model/mappingFormatTokens';
import type { SourcePropertyDefinition } from '../model/types';

export type FormatSelection = { start: number; end: number };
type DomPoint = { node: Node; offset: number };
type DomPiece = { kind: 'text' | 'token' | 'break'; node: Node; start: number; end: number; before: DomPoint; after: DomPoint };

const isElement = (node: Node): node is HTMLElement => node.nodeType === 1;
const isToken = (node: Node): node is HTMLElement => isElement(node) && node.hasAttribute('data-format-token');
const isBlock = (node: Node) => isElement(node) && ['DIV', 'P', 'LI'].includes(node.tagName);

export function renderFormatDom(editor: HTMLElement, text: string, sources: SourcePropertyDefinition[]): void {
  const document = editor.ownerDocument;
  const fragment = document.createDocumentFragment();
  for (const token of tokenizeFormatText(text, sources)) {
    if (token.kind === 'text') {
      fragment.append(document.createTextNode(token.text));
      continue;
    }
    // 빈 text node는 인접한 속성의 앞·사이에서도 문자를 입력할 자리를 제공한다.
    if (fragment.lastChild?.nodeType !== 3) fragment.append(document.createTextNode(''));
    const button = document.createElement('button');
    button.type = 'button';
    button.contentEditable = 'false';
    button.tabIndex = 0;
    button.dataset.formatToken = token.raw;
    button.dataset.sourceProperty = token.sourceProperty;
    button.setAttribute('aria-label', token.text + ' 속성 변경');
    button.setAttribute('aria-haspopup', 'menu');
    button.textContent = token.text;
    fragment.append(button);
  }
  if (fragment.lastChild?.nodeType !== 3) fragment.append(document.createTextNode(''));
  editor.replaceChildren(fragment);
}

function readDom(editor: HTMLElement) {
  let text = '';
  const pieces: DomPiece[] = [];
  const boundaries = new Map<Node, number[]>();
  const append = (node: Node, value: string, kind: DomPiece['kind'], before: DomPoint, after: DomPoint) => {
    const start = text.length;
    text += value;
    pieces.push({ kind, node, start, end: text.length, before, after });
  };
  const visit = (node: Node) => {
    const parent = node.parentNode!;
    const childIndex = Array.prototype.indexOf.call(parent.childNodes, node) as number;
    const before = { node: parent, offset: childIndex };
    const after = { node: parent, offset: childIndex + 1 };
    if (isToken(node)) { append(node, node.dataset.formatToken ?? '', 'token', before, after); return; }
    if (node.nodeType === 3) {
      append(node, node.textContent ?? '', 'text', { node, offset: 0 }, { node, offset: node.textContent?.length ?? 0 });
      return;
    }
    if (isElement(node) && node.tagName === 'BR') {
      // 빈 contenteditable의 browser filler는 입력값이 아니다.
      const filler = parent === editor && editor.childNodes.length === 1;
      append(node, filler ? '' : '\n', 'break', before, after);
      return;
    }
    visitChildren(node);
  };
  const visitChildren = (parent: Node) => {
    const offsets = [text.length];
    const children = Array.from(parent.childNodes);
    children.forEach((child, index) => {
      if (isBlock(child) && text.length > 0 && !text.endsWith('\n')) {
        append(child, '\n', 'break', { node: parent, offset: index }, { node: child, offset: 0 });
      }
      visit(child);
      if (isBlock(child) && index < children.length - 1 && text.length > 0 && !text.endsWith('\n')) {
        append(child, '\n', 'break', { node: child, offset: child.childNodes.length }, { node: parent, offset: index + 1 });
      }
      offsets.push(text.length);
    });
    boundaries.set(parent, offsets);
  };
  visitChildren(editor);
  return { text, pieces, boundaries };
}

export function readFormatDom(editor: HTMLElement): string {
  return readDom(editor).text;
}

export function readFormatSelection(editor: HTMLElement): FormatSelection | null {
  const selection = editor.ownerDocument.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) return null;
  const snapshot = readDom(editor);
  const rawOffset = (node: Node, offset: number, edge: 'start' | 'end') => {
    const token = snapshot.pieces.find((piece) => piece.kind === 'token' && (piece.node === node || piece.node.contains(node)));
    if (token) {
      if (!range.collapsed) return edge === 'start' ? token.start : token.end;
      return offset === 0 ? token.start : token.end;
    }
    const piece = snapshot.pieces.find((item) => item.kind === 'text' && item.node === node);
    if (piece) return piece.start + Math.min(Math.max(offset, 0), piece.end - piece.start);
    const offsets = snapshot.boundaries.get(node);
    return offsets?.[Math.min(Math.max(offset, 0), offsets.length - 1)] ?? 0;
  };
  return { start: rawOffset(range.startContainer, range.startOffset, 'start'), end: rawOffset(range.endContainer, range.endOffset, 'end') };
}

export function restoreFormatSelection(editor: HTMLElement, selection: FormatSelection): void {
  const snapshot = readDom(editor);
  const start = Math.min(Math.max(selection.start, 0), snapshot.text.length);
  const end = Math.min(Math.max(selection.end, start), snapshot.text.length);
  const pointAt = (offset: number, edge: 'start' | 'end'): DomPoint => {
    // 같은 경계에서 text node를 우선하면 속성 버튼 밖에 caret이 남는다.
    const textPiece = snapshot.pieces.find((piece) => piece.kind === 'text' && piece.start <= offset && offset <= piece.end);
    if (textPiece) return { node: textPiece.node, offset: offset - textPiece.start };
    const piece = snapshot.pieces.find((item) => item.start <= offset && offset <= item.end);
    if (piece) {
      if (offset === piece.start) return piece.before;
      if (offset === piece.end) return piece.after;
      if (start === end) return offset - piece.start < (piece.end - piece.start) / 2 ? piece.before : piece.after;
      return edge === 'start' ? piece.before : piece.after;
    }
    return { node: editor, offset: editor.childNodes.length };
  };
  const startPoint = pointAt(start, 'start');
  const endPoint = pointAt(end, 'end');
  const range = editor.ownerDocument.createRange();
  range.setStart(startPoint.node, startPoint.offset);
  range.setEnd(endPoint.node, endPoint.offset);
  const nativeSelection = editor.ownerDocument.getSelection();
  nativeSelection?.removeAllRanges();
  nativeSelection?.addRange(range);
}
